"use client";

import { useUser } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import type {
  Purchase,
  PendingPurchase,
} from "@/components/user-button-pages/helpers";

export type PurchaseHistory = {
  purchases: Purchase[];
  pendingPurchases: PendingPurchase[];
};

/** 사용자별 구매 목록 캐시 키. 다운로드 직후 캐시 갱신(setQueryData)도 이 키를 쓴다. */
export function purchasesQueryKey(userId: string | undefined) {
  return ["purchases", userId] as const;
}

/**
 * `/api/purchases` 공용 조회 (JCG-3516·JCG-3854). 설정 구매 목록과 대시보드 PDF 위젯이 함께 쓴다.
 *
 * 실패를 빈 목록으로 돌려주지 않고 error로 던져, 호출부가 로딩·빈 구매·조회 실패를 구분하게 한다.
 * Clerk 로딩 중이거나 비로그인이면 조회하지 않는다(isPending 유지). 키에 사용자 id가 있어 계정이
 * 바뀌면 이전 사용자의 목록 대신 새로 조회한다.
 */
export function usePurchasesQuery() {
  const { user } = useUser();
  return useQuery({
    queryKey: purchasesQueryKey(user?.id),
    enabled: !!user,
    queryFn: async (): Promise<PurchaseHistory> => {
      // JCG-2074: 이메일을 쿼리로 넘기지 않고 서버가 Clerk 세션으로 본인 식별
      const response = await fetch("/api/purchases", { cache: "no-store" });
      if (!response.ok) throw new Error("구매 목록 조회 실패");
      const result = await response.json();
      return {
        purchases: result.purchases ?? [],
        pendingPurchases: result.pendingPurchases ?? [],
      };
    },
    retry: 1,
    // JCG-3516: 앱 기본값(24시간 fresh, 마운트 재조회 안 함)을 따르면 결제 직후 client 이동으로
    // 열 때 이전 목록이 남는다. 열 때마다 다시 조회하고, 실패하면 이전 목록을 유지한다.
    refetchOnMount: "always",
  });
}
